import { backgroundFlexibleProp, backgroundTypeProp, mediaQueryProp } from 'utils/proptypes/modules/mediaProps';
import { headingSizeProp, headingTagProp, wysiwygProp } from 'utils/proptypes/modules/textProps';

import Above from 'components/breakpoints/Above';
import AnimationWrapper from 'components/wrappers/AnimationWrapper';
import AspectWrapper from 'components/wrappers/AspectWrapper';
import BackgroundSwitch from 'components/background/BackgroundSwitch';
import Below from 'components/breakpoints/Below';
import ButtonsList from 'components/buttons/ButtonsList';
import Heading from 'components/text/Heading';
import Paragraph from 'components/text/Paragraph';
import PropTypes from 'prop-types';
import React from 'react';
import Tag from 'components/text/Tag';
import { TextLinkWysisyg } from 'components/text/TextLink';
import Wysiwyg from 'libs/wordpress/components/Wysiwyg';
import { above } from 'utils/mediaqueries';
import { buttonsProp } from 'utils/proptypes/modules/buttonsProps';
import styled from 'libs/styled';

const Div = styled('div')``;

const Wrapper = styled('div')`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    height: 100%;
`;

const TextAndButtons = styled('div')`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
    padding: 40px 0 0;

    ${above.sm} {
        justify-content: flex-end;
        padding: 40px 16px 0;
    }

    ${above.md} {
        width: 50%;
        padding: 0 16px;
    }
`;

const BackgroundWrapper = styled('div')`
    display: flex;
    align-items: flex-end;
    width: 100%;
    margin-bottom: 16px;

    ${above.sm} {
        padding: 0 16px;
        margin-bottom: 0;
    }
`;

const BackgroundAnimationWrapper = styled(AnimationWrapper)`
    display: flex;
    align-items: flex-end;
    width: 50%;
`;
/**
 *  contentBlock used to display heading, text, buttons and background.
 *
 * @param {object} [background] - Background object.
 * @param {string} backgroundType - Type of background if not supplied inside the background object.
 * @param {string} button - Main button, used as link.
 * @param {string} heading - Heading as a string.
 * @param {string} headingSize - The size of heading passed as props to Heading.
 * @param {string} headingTag - The html-tag used by heading; h1, h2, h3 etc.
 * @param {array} mediaQuery - Array of media-queries used by background image.
 * @param {string} tag - Small tag displayed above heading.
 * @param {array} [text] - Text as a wysiwyg-array.
 * @param {string} textColor - Color of all texts.
 */

const CallToActionColumn = ({
    background,
    backgroundRatio,
    backgroundType,
    buttons,
    heading,
    headingSize,
    headingTag,
    mediaQuery,
    tag,
    text,
    textColor,
}) => (
    <Wrapper color={textColor}>
        <Div marginBottom={['12px', null, '24px']}>
            {background && (
                <Below
                    breakpoint="sm"
                    render={() => (
                        <BackgroundWrapper>
                            <AspectWrapper height="auto !important" ratio={backgroundRatio} width="100%">
                                <BackgroundSwitch
                                    type={backgroundType}
                                    background={background}
                                    mediaQuery={mediaQuery}
                                />
                            </AspectWrapper>
                        </BackgroundWrapper>
                    )}
                />
            )}
            <Heading withAnimation as={headingTag} margin="0" size={headingSize}>
                {heading}
            </Heading>
            <Tag withAnimation marginTop={['16px', null, '24px']}>
                {tag}
            </Tag>
        </Div>
        <Div display="flex" flexDirection={['column', null, 'row']} mx={[null, null, '-16px']}>
            <TextAndButtons>
                {text && (
                    <AnimationWrapper>
                        <Wysiwyg
                            data={text}
                            textComponent={props => (
                                <Paragraph size="sm-custom" marginBottom={['24px', null, '40px']} {...props} />
                            )}
                            tagComponents={{ url: TextLinkWysisyg }}
                        />
                    </AnimationWrapper>
                )}
                <ButtonsList withAnimation buttons={buttons} />
            </TextAndButtons>
            {background && (
                <Above
                    breakpoint="sm"
                    render={() => (
                        <BackgroundAnimationWrapper>
                            <AspectWrapper height="auto !important" ratio={backgroundRatio} width="100%">
                                <BackgroundSwitch
                                    type={backgroundType}
                                    background={background}
                                    mediaQuery={mediaQuery}
                                />
                            </AspectWrapper>
                        </BackgroundAnimationWrapper>
                    )}
                />
            )}
        </Div>
    </Wrapper>
);

CallToActionColumn.propTypes = {
    background: backgroundFlexibleProp,
    backgroundRatio: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]).isRequired,
    backgroundType: backgroundTypeProp.isRequired,
    buttons: buttonsProp.isRequired,
    heading: PropTypes.string.isRequired,
    headingSize: headingSizeProp.isRequired,
    headingTag: headingTagProp.isRequired,
    mediaQuery: mediaQueryProp.isRequired,
    tag: PropTypes.string.isRequired,
    text: wysiwygProp,
    textColor: PropTypes.string.isRequired,
};

export default CallToActionColumn;
